import { CAMPAIGN_STATUS, formatIst, type Campaign } from '@wa/shared';

/**
 * What a campaign is doing, in one word — and where a word is not enough, the
 * sentence that replaces a support call.
 *
 * A held campaign is the case that matters. "9,300 of 20,000 sent, the rest after
 * 14:20" is something a client can plan around; "11,700 failed" is somebody
 * ringing to ask what went wrong.
 */
export function CampaignStatusChip({ campaign }: { campaign: Campaign }) {
  const tone: Record<number, string> = {
    0: 'border-(--color-line) text-(--color-muted)',
    1: 'border-(--color-brand) text-(--color-brand)',
    2: 'border-(--color-brand) bg-(--color-brand-wash) text-(--color-brand)',
    3: 'border-(--color-warn) text-(--color-warn)',
    4: 'border-(--color-good) text-(--color-good)',
    5: 'border-(--color-line) text-(--color-muted)',
  };

  const held = campaign.status === 2 && campaign.resumeAfter;
  const label = held ? 'Waiting' : CAMPAIGN_STATUS[campaign.status];

  return (
    <span
      className={`shrink-0 rounded-full border px-2 py-0.5 text-micro font-medium ${
        held ? 'border-(--color-warn) text-(--color-warn)' : tone[campaign.status]
      }`}
    >
      {label}
    </span>
  );
}

/** The one-line explanation under a campaign that is not simply running. */
export function campaignNote(campaign: Campaign): string | null {
  if (campaign.status === 2 && campaign.resumeAfter) {
    return `This number has reached its 24-hour limit. The rest goes out after ${formatIst(campaign.resumeAfter)}.`;
  }

  if (campaign.status === 3) {
    switch (campaign.pauseReason) {
      case 'wallet':
        return 'Paused: the wallet ran out. Top up and resume — nothing was lost, and it carries on where it stopped.';
      case 'tier':
        return 'Paused at this number’s 24-hour limit.';
      default:
        return 'Paused. Resume when you are ready; the rest is still queued.';
    }
  }

  if (campaign.status === 1 && campaign.scheduledAt) {
    return `Goes out ${formatIst(campaign.scheduledAt)}. The audience is worked out then, so anyone who qualifies before it fires is included.`;
  }

  return null;
}
